import * as yup from 'yup';

import { QALog } from 'types/QALog';

import { QAItem } from './AddQALogModal.props';

export const initialValues = {
  employee: '',
  date: new Date(),
  hours: '',
  items: [],
  notes: '',
  addToPackages: false,
  combineLeftovers: false,
};

export const QA_LOG_LEFTOVERS_QUERY_KEY = 'fetch-qa-log-leftovers';

const leftoverSkuId = (sku?: string | { _id?: string }) => {
  if (!sku) {
    return '';
  }

  if (typeof sku === 'string') {
    return sku;
  }

  return sku._id || '';
};

export const sumLeftoverQuantitiesBySku = (
  logs: QALog[] = [],
): Record<string, number> =>
  logs.reduce<Record<string, number>>((totals, log) => {
    const leftovers = log.passedItemsLeftOverAfterRedeploy;

    if (!Array.isArray(leftovers) || leftovers.length === 0) {
      return totals;
    }

    leftovers.forEach((item) => {
      const skuId = leftoverSkuId(item.sku);
      const quantity = Number(item.quantity) || 0;

      if (!skuId || quantity <= 0) {
        return;
      }

      totals[skuId] = (totals[skuId] || 0) + quantity;
    });

    return totals;
  }, {});

export const getCreatedQaLogId = (result: any) =>
  result?.item?._id || result?._id || '';

export const buildReadyPackagesPayload = (
  washHub: string,
  items: QAItem[] = [],
  options?: {
    qaLog?: string;
    leftoverBySku?: Record<string, number>;
    combineLeftovers?: boolean;
  },
) => {
  const leftoverBySku = options?.leftoverBySku || {};
  const combineLeftovers = Boolean(options?.combineLeftovers);
  const skuIds = new Set(
    items.map((item) => item.sku).filter(Boolean) as string[],
  );

  if (combineLeftovers) {
    Object.keys(leftoverBySku).forEach((skuId) => skuIds.add(skuId));
  }

  const packageItems = Array.from(skuIds)
    .map((sku) => {
      const passed =
        Number(items.find((item) => item.sku === sku)?.passed) || 0;
      const leftover = combineLeftovers ? leftoverBySku[sku] || 0 : 0;

      return {
        sku,
        quantity: passed + leftover,
      };
    })
    .filter((item) => item.quantity > 0);

  return {
    washHub,
    items: packageItems,
    ...(options?.qaLog ? { qaLog: options.qaLog } : {}),
    ...(combineLeftovers ? { usingQaLeftovers: true } : {}),
  };
};

export const validationSchema = yup.object().shape({
  employee: yup.string().required('Employee is required'),
  hours: yup
    .number()
    .typeError('Worked Hours must be a number')
    .required('Worked Hours is required'),
  items: yup
    .array()
    .of(
      yup.object().shape({
        sku: yup.string().required('SKU is required'),
        passed: yup
          .number()
          .typeError('Passed value must be a number')
          .required('Passed value is required'),
        dirty: yup
          .number()
          .typeError('Dirty value must be a number')
          .required('Dirty value is required'),
        damaged: yup
          .number()
          .typeError('Damaged value must be a number')
          .required('Damaged value is required'),
      }),
    )
    .required('Items are required')
    .min(1, 'At least one item must be selected'),
});
